import { useMemo, useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCostSummary, useRecipeIngredients } from "@/hooks/recipes/queries";
import { useUoms } from "@/hooks/useUomReference";
import { ingredientLineName } from "@/types/recipe";
import type { Recipe, RecipeIngredient } from "@/types/recipe";
import { formatTimer, type RecipeStep } from "@/components/recipes/execution-steps";
import { richTextHtml } from "@/lib/rich-text";
import {
  cardEquivalent,
  kitchenAmount,
  multipleLabel,
  scaledYield,
  type BatchScale,
} from "@/lib/recipes/staged-method";
import { LineAllergens } from "./AllergenSymbol";
import { StepPhotos } from "./StepPhotos";
import { CcpStepFlag, CcpStepPrintNote, CcpWarningBadge } from "./ccp";
import { ccpStepCount, ccpStepNumber, isCcpStep } from "@/lib/recipes/ccp";
import { BatchScaleControl } from "./method/BatchScaleControl";

interface RecipeCardViewProps {
  recipe: Recipe;
}

/**
 * The recipe as a cook needs it at the pass: what goes in, and what to do —
 * on one sheet of paper (T-806).
 *
 * Everything the detailed view carries for the people who *build* recipes —
 * costs, versions, availability scopes, containers, shelf life — is left out
 * on purpose. Photos are on the screen card but not on paper, as is the app's
 * own chrome, via the print rules in index.css keyed on `data-print-region`.
 * The cook can scale the batch, and each line shows its weight and volume.
 */
export function RecipeCardView({ recipe }: RecipeCardViewProps) {
  const { t } = useLingui();

  // Same source cook mode uses: the detail payload does not carry ingredients
  // (T-801), they come from GET /recipes/:id/ingredients.
  const { data: ingredientsResponse, isLoading: ingredientsLoading } = useRecipeIngredients(
    recipe.id,
  );
  const ingredients: RecipeIngredient[] = ingredientsResponse?.data ?? recipe.ingredients ?? [];
  const [scale, setScale] = useState<BatchScale>(1);
  const { data: uoms } = useUoms();
  // Each line's weight and volume come from the cost summary. Extra detail: the
  // card draws without it and fills in when it lands.
  const { data: costSummary } = useCostSummary(recipe.id);
  const measures = useMemo(
    () => new Map(costSummary?.lines.map((line) => [line.ingredientId, line.measures ?? null])),
    [costSummary],
  );
  const amount = (ing: RecipeIngredient) =>
    // As the editor saved it at the recipe's own size; scaled amounts read like a kitchen sheet.
    scale === 1 ? `${ing.quantity} ${ing.unit}` : kitchenAmount(Number(ing.quantity) * scale, ing.unit);

  const steps: RecipeStep[] = Array.isArray(recipe.instructions)
    ? (recipe.instructions as RecipeStep[])
    : [];

  const totalMinutes = (recipe.prepTimeMinutes ?? 0) + (recipe.cookTimeMinutes ?? 0);

  const meta: { label: string; value: string }[] = [];
  if (recipe.yieldQuantity)
    meta.push({
      label: t`Yield`,
      value: `${scaledYield(recipe.yieldQuantity, scale) ?? recipe.yieldQuantity} ${recipe.yieldUom ?? ""}`.trim(),
    });
  if (scale !== 1)
    // On paper the card has no control to show which batch it is for.
    meta.push({ label: t`Batch`, value: scale === 0.5 ? t`Half` : multipleLabel(scale) });
  if (recipe.portionQuantity)
    meta.push({
      label: t`Portion`,
      value: `${recipe.portionQuantity} ${recipe.portionUom ?? ""}`.trim(),
    });
  if (recipe.servings) meta.push({ label: t`Servings`, value: String(recipe.servings) });
  if (recipe.prepTimeMinutes)
    meta.push({ label: t`Prep`, value: t`${recipe.prepTimeMinutes} min` });
  if (recipe.cookTimeMinutes)
    meta.push({ label: t`Cook`, value: t`${recipe.cookTimeMinutes} min` });
  if (totalMinutes > 0) meta.push({ label: t`Total`, value: t`${totalMinutes} min` });

  return (
    <article
      data-print-region
      aria-label={t`Recipe card`}
      className="mx-6 mb-10 flex flex-col gap-5 rounded-xl border border-(--color-border) bg-(--color-surface-content) p-6 print:m-0 print:gap-3 print:rounded-none print:border-0 print:p-0 print:text-[9.5pt]"
    >
      {/* Title block */}
      <header className="flex items-start justify-between gap-4 border-b border-(--color-border) pb-4 print:pb-2">
        {recipe.imageUrl && (
          <img src={recipe.imageUrl} alt="" className="size-20 shrink-0 rounded-xl object-cover print:hidden" />
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className="text-2xl font-semibold text-(--color-text-primary) print:text-[16pt]">
            {recipe.name}
          </h1>
          <p className="text-sm text-(--color-text-secondary) print:text-[8pt]">
            {t`RCP-${recipe.id}`} · {t`Version ${recipe.version}`}
          </p>
          <CcpWarningBadge count={ccpStepCount(recipe.instructions)} className="self-start" />
          {recipe.description && (
            <div
              className="mt-1 text-sm text-(--color-text-secondary) print:text-[8.5pt] [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
              dangerouslySetInnerHTML={{ __html: richTextHtml(recipe.description) }}
            />
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 print:hidden">
          <BatchScaleControl
            scale={scale}
            onScaleChange={setScale}
            yieldQuantity={recipe.yieldQuantity}
            yieldUom={recipe.yieldUom}
          />
          <Button variant="brand-outline" onClick={() => window.print()} className="shrink-0 print:hidden">
            <Printer className="size-5" />
            {t`Print`}
          </Button>
        </div>
      </header>

      {/* Facts a cook checks before starting */}
      {meta.length > 0 && (
        <dl className="flex flex-wrap gap-x-6 gap-y-2 print:gap-x-4 print:gap-y-1">
          {meta.map((item) => (
            <div key={item.label} className="flex items-baseline gap-1.5">
              <dt className="text-xs uppercase tracking-wide text-(--color-text-secondary) print:text-[7.5pt]">
                {item.label}
              </dt>
              <dd className="text-sm font-medium text-(--color-text-primary) print:text-[8.5pt]">
                {item.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {/* Ingredients */}
      <section aria-label={t`Ingredients`} className="flex flex-col gap-2 break-inside-avoid">
        <h2 className="text-lg font-semibold text-(--color-text-primary) print:text-[11pt]">
          {t`Ingredients`}
        </h2>
        {ingredientsLoading ? (
          <p className="text-sm text-(--color-text-secondary)" aria-busy="true">{t`Loading…`}</p>
        ) : ingredients.length > 0 ? (
          <ul className="grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-2 print:grid-cols-2">
            {ingredients.map((ing) => {
              const equivalent = cardEquivalent(Number(ing.quantity), ing.unit, measures.get(ing.id), scale, uoms);
              return (
                <li
                  key={ing.id}
                  className="flex items-baseline justify-between gap-3 break-inside-avoid border-b border-dotted border-(--color-border) py-1 text-sm print:py-0.5 print:text-[8.5pt]"
                >
                  <span className="min-w-0 text-(--color-text-primary)">
                    {ingredientLineName(ing)}
                    {ing.isOptional && (
                      <span className="text-(--color-text-secondary)"> ({t`optional`})</span>
                    )}
                    {ing.preparationNotes && (
                      <span className="text-(--color-text-secondary)">, {ing.preparationNotes}</span>
                    )}{" "}
                    <LineAllergens line={ing} />
                  </span>
                  <span className="shrink-0 text-right font-medium text-(--color-text-primary)">
                    <span>{amount(ing)}</span>
                    {equivalent && (
                      <span className="block text-xs font-normal text-(--color-text-secondary) print:text-[7.5pt]">{equivalent}</span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-(--color-text-secondary)">{t`No ingredients listed.`}</p>
        )}
      </section>

      {/* Steps */}
      <section aria-label={t`Steps`} className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold text-(--color-text-primary) print:text-[11pt]">
          {t`Steps`}
        </h2>
        {steps.length > 0 ? (
          <ol className="flex flex-col gap-2 print:gap-1">
            {steps.map((step, i) => (
              <li key={i} className="flex gap-3 break-inside-avoid print:gap-2">
                <span className="w-6 shrink-0 text-sm font-semibold text-(--color-text-secondary) print:w-5 print:text-[8.5pt]">
                  {i + 1}.
                </span>
                <div className="flex min-w-0 flex-col gap-0.5">
                  {isCcpStep(step) ? (
                    <div className="flex items-start gap-2">
                      <p className="text-sm text-(--color-text-primary) print:text-[8.5pt]">
                        {step.instruction || t`Untitled step`}
                      </p>
                      <CcpStepFlag
                        recipeId={recipe.id}
                        number={ccpStepNumber(step, i)}
                        displayNumber={i + 1}
                        text={step.instruction ?? ""}
                      />
                    </div>
                  ) : (
                    <p className="text-sm text-(--color-text-primary) print:text-[8.5pt]">
                      {step.instruction || t`Untitled step`}
                    </p>
                  )}
                  {step.description && (
                    // Saved as sanitized rich text by the step editor; rendered
                    // the way cook mode does, so tags don't print literally.
                    <div
                      className="text-sm text-(--color-text-secondary) print:text-[8pt] [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
                      dangerouslySetInnerHTML={{ __html: richTextHtml(step.description) }}
                    />
                  )}
                  <StepFacts step={step} />
                  <StepPhotos urls={step.mediaUrls} />
                  {isCcpStep(step) && <CcpStepPrintNote text={step.instruction ?? ""} className="mt-0.5" />}
                  {step.warningEnabled && step.warnings && (
                    <p className="text-sm font-medium text-(--color-danger-text) print:text-[8pt]">
                      {t`Warning:`} {step.warnings}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-(--color-text-secondary)">{t`No execution steps defined yet.`}</p>
        )}
      </section>
    </article>
  );
}

/**
 * Timer and temperature on one line. A cook working from paper cannot start a
 * clock, so the duration is stated rather than counted down.
 */
function StepFacts({ step }: { step: RecipeStep }) {
  const { t } = useLingui();

  const facts: string[] = [];
  if (step.timerEnabled && (step.timer ?? 0) > 0) {
    facts.push(`${t`Time:`} ${formatTimer(step.timer!, step.timerUnit)}`);
  }
  if (step.temperature) {
    facts.push(`${t`Temp:`} ${step.temperature}°${step.temperatureUnit ?? "C"}`);
  }
  if (step.techniqueNotes) {
    facts.push(step.techniqueNotes);
  }

  if (facts.length === 0) return null;

  return (
    <p className="text-xs text-(--color-text-secondary) print:text-[7.5pt]">{facts.join(" · ")}</p>
  );
}
